import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Phone, MessageSquare, Zap, Users, Clock, Globe, ArrowRight } from 'lucide-react';

const stats = [
  { value: '50,000+', label: 'Calls Handled', icon: Phone },
  { value: '12,000+', label: 'Leads Captured', icon: Users },
  { value: '99.9%', label: 'Uptime', icon: Clock },
  { value: '10+', label: 'Languages', icon: Globe },
];

const capabilities = [
  {
    icon: Phone,
    title: 'Inbound & Outbound Calls',
    description: 'AI handles every call — answers questions, qualifies leads, books appointments, and routes urgent matters to your team.',
    color: 'from-purple-600 to-indigo-600',
    badgeColor: 'bg-purple-500/10 text-purple-400',
  },
  {
    icon: MessageSquare,
    title: 'Live Chat Widget',
    description: 'Embed an AI chat agent on your website that captures leads, tracks marketing sources, and engages visitors 24/7.',
    color: 'from-blue-600 to-cyan-600',
    badgeColor: 'bg-blue-500/10 text-blue-400',
  },
  {
    icon: Zap,
    title: 'Sales Outreach Campaigns',
    description: 'AI-generated multi-step outreach sequences with call scripts, email templates, and automated follow-ups.',
    color: 'from-amber-600 to-orange-600',
    badgeColor: 'bg-amber-500/10 text-amber-400',
  },
];

const VideoSection = () => {
  const navigate = useNavigate();

  return (
    <section className="py-20 px-6" data-testid="video-section">
      <div className="max-w-7xl mx-auto">
        {/* Stats bar */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-20" data-testid="stats-bar">
          {stats.map((stat) => (
            <div key={stat.label} className="text-center p-6 rounded-2xl bg-white/[0.02] border border-white/[0.06] backdrop-blur-sm" data-testid={`stat-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}>
              <stat.icon className="w-5 h-5 text-purple-400 mx-auto mb-3" />
              <p className="text-3xl font-bold text-white mb-1">{stat.value}</p>
              <p className="text-sm text-gray-500">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Section Header */}
        <div className="text-center mb-14">
          <p className="text-purple-400 text-sm font-semibold tracking-widest uppercase mb-3">
            One Platform, Three Channels
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
            Meet your AI-powered front desk
          </h2>
          <p className="text-base text-gray-400 max-w-2xl mx-auto">
            Miadesk AI Agent handles calls, chats, and outreach — all from a single dashboard. Never miss a customer again.
          </p>
        </div>

        {/* Capabilities grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12" data-testid="capabilities-grid">
          {capabilities.map((cap) => (
            <div key={cap.title} className="group relative rounded-2xl bg-white/[0.02] border border-white/[0.06] p-7 hover:border-purple-500/20 transition-all duration-300" data-testid={`capability-${cap.title.toLowerCase().replace(/\s+/g, '-')}`}>
              <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${cap.color} flex items-center justify-center mb-5 group-hover:scale-110 transition-transform`}>
                <cap.icon className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2">{cap.title}</h3>
              <p className="text-sm text-gray-400 leading-relaxed">{cap.description}</p>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="flex flex-col items-center gap-3">
          <Button
            onClick={() => navigate('/register')}
            className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white rounded-full px-8 py-6 text-base font-semibold shadow-lg shadow-purple-500/25 transition-all"
            data-testid="video-cta-btn"
          >
            Start Free Trial
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
          <span className="text-sm text-gray-500">
            Try free for 7 days. No credit card required.
          </span>
        </div>
      </div>
    </section>
  );
};

export default VideoSection;
